import React from 'react';
import {
  Box,
  Stack,
  Typography,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
 
import { darkTheme, lightTheme, AppThemeMode } from '../theme';
import Navbar from '../components/Navbar';
import { readTrapsInReview } from '../firebase/dataAccess';
import TrapRow from '../components/trapRow';

type Trap = {
  id: string;
  name: string;
  creators: string[];
  dateInvented: string;
  type: string,
  thumbnailUrl?: string;
  minigame: string,
  rating?: { average: number; count: number };
  tierlistRating?: { average: number; count: number };
  commentCount?: number;
  video?: { youtubeId?: string; embedUrl?: string; watchUrl?: string } | null;
};

const useFetchTrapsInReview = () => {
  const [traps, setTraps] = React.useState<Trap[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = (await readTrapsInReview()) as unknown as Trap[];
        if (!cancelled) setTraps(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  return { traps, loading, error };
};

const ReviewTraps: React.FC = () => {
  const [mode, setMode] = React.useState<AppThemeMode>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem('themeMode') as AppThemeMode | null;
        if (stored === 'light' || stored === 'dark') return stored;
      } catch {}
    }
    return 'dark';
  });
  const currentTheme = mode === 'light' ? lightTheme : darkTheme;
  const toggleMode = React.useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);
  React.useEffect(() => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('themeMode', mode);
    } catch {}
  }, [mode]);

  const theme = currentTheme;
  const { traps: fetchedTraps, loading: trapsLoading, error } = useFetchTrapsInReview();
  const permissionDenied = Boolean(error && /permission|insufficient/i.test(error));

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <Navbar mode={mode} onToggleTheme={toggleMode} />
      <Box sx={{ height: 96 }} />
      {permissionDenied ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
          <Typography variant="body1">Admins only.</Typography>
        </Box>
      ) : (
        <Box sx={{
          maxWidth: 1040,
          mx: 'auto',
          px: 2,
          pb: 6,
          position: 'relative',
        }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 17,
              right: 17,
              bottom: 0,
              bgcolor: theme.palette.background.default,
              borderRadius: 2,
              zIndex: -1,
            }}
          />
          <Stack spacing={2} sx={{ position: 'relative', zIndex: 1, mt: -1.15 }}>

            {trapsLoading && (
              <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                Loading traps in review...
              </Typography>
            )}
            {error && !permissionDenied && (
              <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                Failed to load traps: {error}
              </Typography>
            )}

            {!trapsLoading && !error && fetchedTraps.map((trap) => (
              <TrapRow key={trap.id} trap={trap} />
            ))}

            {!trapsLoading && !error && fetchedTraps.length === 0 && (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No traps in review.
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </ThemeProvider>
  );
};

export default ReviewTraps;